<template>
    <HeaderTableBlock
        :title="$t('tiktok.table.title')"
        :count-title="pagination?.total"
        messenger="tiktok"
        :config="TIKTOK"
        :numeric-only="false"
        :min-length="1"
        :max-length="30"
        :loading="loading"
        :send-input="searchInput"
        @submitInput="submitInput"
    />

    <component
        :is="tableState"
        :account-list="accountList"
        :table-type="type"
        :table-status="tableStatus"
        @loadMore="loadMore"
    >
        <!-- STATUS -->
        <template #status="{ account }">
            <button
                class="text-uppercase"
                style="text-wrap: balance"
                @click="onStatus(account)"
            >
                {{ statusLabel(statusAccount(account)?.status) }}
            </button>
        </template>

        <!-- SETTINGS -->
        <template #settings="{ account }">
            <div class="inside_block" @click="onAccountSettings(account)">
                <BIconGear class="icon"/>
            </div>
        </template>

        <!-- ACTIONS -->
        <template #actions="{ account }">
            <button
                v-if="isActiveAccount(account)"
                class="text-uppercase"
                @click="onLogout(account)"
            >
                {{ $t("tiktok.logout.button") }}
            </button>

            <ButtonLoading
                v-else
                variant="outline"
                class="text-uppercase"
                :loading="isLoadingButton(account)"
                @click="onAccountAction(account)"
            >
                {{ statusLabel(statusAccount(account)?.buttonText) }}
            </ButtonLoading>
        </template>

        <!-- PAGINATION IN SMALL AND MEDIUM SCREENS -->
        <template #pagination>
            <BasePagination
                v-show="!tableStatus"
                :lastPage="pagination?.last_page"
                :currentPage="pagination?.current_page"
                @onChangePage="changePage"
            />
        </template>
    </component>
</template>

<script setup>
// core
import {ref, computed, watchEffect, getCurrentInstance} from "vue";
import {useStore} from "vuex";
import {useRouter} from "vue-router";

// components
import HeaderTableBlock from "@/components/ui/table/HeaderTableBlock.vue";
import ButtonLoading from "@/components/Recurring/ButtonLoading.vue";
import DesktopTiktokAccountsTable from "./DesktopTiktokAccountsTable.vue";
import MobileTiktokAccountsTable from "./MobileTiktokAccountsTable.vue";
import BasePagination from "@/components/ui/BasePagination.vue";

// configs
import statusAccountConfig from "../configs/statusAccountConfig";
import useAccountStatusLabel from "../configs/useAccountStatusLabel";
import {TIKTOK} from "@/data/messengers.js";

const store = useStore();
const {proxy} = getCurrentInstance();
const router = useRouter();
const {statusLabel} = useAccountStatusLabel();

const ACTIVE_STATUS = "active";

const windowSize = ref(window.innerWidth);
const loadingAccount = ref("");

const props = defineProps({
    accountList: {
        type: Array,
        default: () => [],
    },
    type: {
        type: String, // 'visible' || 'invisible'
        default: "visible",
    },
});

const isVisibleTable = computed(() => props.type === "visible");
const visibilityIndex = computed(() => (isVisibleTable.value ? 0 : 1));

const pagination = computed(
    () => store.state.tiktok.pagination[visibilityIndex.value]
);
const loading = computed(() => store.state.tiktok.loading[visibilityIndex.value]);
const searchInput = computed(
    () => store.state.tiktok.searchInput[visibilityIndex.value]
);

const tableState = computed(() =>
    windowSize.value >= 1200 ? DesktopTiktokAccountsTable : MobileTiktokAccountsTable
);

/**
 * "loading" while the request is in flight, "noAccounts" when the answer is
 * empty, null when there is something to render.
 */
const tableStatus = computed(() => {
    if (loading.value) return "loading";
    if (!props.accountList?.length) return "noAccounts";
    return null;
});

const statusAccount = (account) => statusAccountConfig(account);

const isLoadingButton = (account) => loadingAccount.value === account.user_id;

const submitInput = (input) => {
    store.dispatch("tiktok/setSearchInput", {
        input,
        visibility: isVisibleTable.value,
    });

    store.dispatch("tiktok/loadAccounts", {
        username: input,
        visibility: isVisibleTable.value,
    });
};

const loadMore = () => {
    const pageData = pagination.value;
    const isLastPage = pageData?.current_page === pageData?.last_page;

    if (isLastPage) return;

    store.dispatch("tiktok/loadAccounts", {
        username: searchInput.value || null,
        page: pageData.current_page + 1,
        visibility: isVisibleTable.value,
    });
};

const changePage = (numberPage) => {
    store.dispatch("tiktok/loadAccounts", {
        username: searchInput.value || null,
        page: numberPage,
        visibility: isVisibleTable.value,
        isCollectData: false, // paged view: previous pages are not kept
    });
};

const reloadBothTables = () =>
    Promise.all([
        store.dispatch("tiktok/loadAccounts", {visibility: true}),
        store.dispatch("tiktok/loadAccounts", {visibility: false}),
    ]);

const onStatus = (account) => {
    switch (statusAccount(account)?.status) {
        case "waiting_for": // not connected to a CRM
            store.commit("openDialog", {
                name: "WhichCrmSystemDialog",
                params: {messenger: "tiktok"},
            });
            break;
        case "requires_activation":
            router.push({name: "TiktokActivation"});
            break;
        case "number_disable":
            store.commit("openDialog", {
                name: "InformationDialog",
                info: "accountDisabled",
            });
            break;
        case "error":
            store.commit("openDialog", "HelpDialog");
            break;
    }
};

const isActiveAccount = (account) =>
    account.status === ACTIVE_STATUS && account.visibility;

const onLogout = (account) => {
    store.commit("openDialog", {
        name: "TiktokLogoutDialog",
        info: {
            user_id: account.user_id,
            username: account.username,
        },
    });
};

const onAccountSettings = (account) => {
    store.commit("openDialog", {
        name: "TiktokAccountSettingsDialog",
        info: {
            user_id: account.user_id,
            username: account.username,
            visibility: account.visibility,
            webhooks: account.webhooks ?? [],
            api_key: account.api_key,
            tags: account.tags ?? [],
        },
    });
};

const onAccountAction = async (account) => {
    switch (statusAccount(account)?.buttonText) {
        case "pay_button":
            router.push({name: "Tariffs"});
            break;
        case "activation_button":
            router.push({name: "TiktokActivation"});
            break;
        case "return":
            loadingAccount.value = account.user_id;
            try {
                await store.dispatch("tiktok/updateVisibility", {
                    userId: account.user_id,
                    visibility: true,
                    moshaToast: proxy.$moshaToast,
                });
                await reloadBothTables();
            } finally {
                loadingAccount.value = "";
            }
            break;
        case "help_button":
        case "error":
        default:
            store.commit("openDialog", "HelpDialog");
            break;
    }
};

watchEffect(() => {
    window.addEventListener("resize", () => {
        windowSize.value = window.innerWidth;
    });
});
</script>

<style lang="scss" scoped>
@use "@/styles/table.scss";
</style>
